import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Collapse,
  IconButton,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import useFetchDatav2 from '../hooks/useFetchDatav2';

const RISK_COLORS = {
  NONE: 'success',
  LOW: 'info',
  HIGH: 'error',
};

const FIELD_LABELS = {
  DISPLAY_NAME: 'Name',
  SPECIALIZATION: 'Specialization',
};

const STATUS_LABELS = {
  DRAFT: 'Draft',
  REVIEW: 'Review',
  ISSUED: 'Issued',
};

function ChangeRequestDashboard({ cohortId }) {
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const queryClient = useQueryClient();

  const { data: summaryData, isLoading } = useFetchDatav2(
    ['change-requests-summary', cohortId],
    `/cohorts/${cohortId}/certificate-change-requests/summary`,
    {},
    'Could not fetch change request summary',
    !!cohortId,
    true
  );

  const groups = summaryData?.data?.groups || [];
  const totalPending = summaryData?.data?.total_pending || 0;

  const invalidateQueries = () => {
    queryClient.invalidateQueries(['change-requests-summary', cohortId]);
    queryClient.invalidateQueries(['certificates-summary', cohortId]);
    queryClient.invalidateQueries(['certificates', cohortId]);
  };

  const batchApproveMutation = useMutation(
    (data) =>
      axiosInstancev2.post(
        `/cohorts/${cohortId}/certificate-change-requests/batch-approve`,
        data
      ),
    {
      onSuccess: (_, variables) => {
        toast.success(
          `Batch approved all ${variables.category} (${
            variables.field_name || 'all fields'
          })`
        );
        invalidateQueries();
        setConfirmDialogOpen(false);
      },
      onError: (error) => {
        toast.error(
          `Batch approve failed. ${error?.response?.data?.error || ''}`
        );
      },
    }
  );

  const batchRejectMutation = useMutation(
    (data) =>
      axiosInstancev2.post(
        `/cohorts/${cohortId}/certificate-change-requests/batch-reject`,
        data
      ),
    {
      onSuccess: () => {
        toast.success('Batch reject completed');
        invalidateQueries();
        setRejectDialogOpen(false);
        setRejectReason('');
      },
      onError: (error) => {
        toast.error(
          `Batch reject failed. ${error?.response?.data?.error || ''}`
        );
      },
    }
  );

  const individualApproveMutation = useMutation(
    (id) => axiosInstancev2.post(`/certificate-change-requests/${id}/approve`),
    {
      onSuccess: () => {
        toast.success('Change request approved');
        invalidateQueries();
      },
      onError: (error) => {
        toast.error(`Approve failed. ${error?.response?.data?.error || ''}`);
      },
    }
  );

  const individualRejectMutation = useMutation(
    ({ id, reason }) =>
      axiosInstancev2.post(`/certificate-change-requests/${id}/reject`, {
        reason,
      }),
    {
      onSuccess: () => {
        toast.success('Change request rejected');
        invalidateQueries();
        setRejectDialogOpen(false);
        setRejectReason('');
      },
      onError: (error) => {
        toast.error(`Reject failed. ${error?.response?.data?.error || ''}`);
      },
    }
  );

  const handleBatchApprove = (category, fieldName) => {
    setConfirmTarget({ category, field_name: fieldName });
    setConfirmDialogOpen(true);
  };

  const handleConfirmBatchApprove = () => {
    batchApproveMutation.mutate(confirmTarget);
  };

  const handleOpenRejectDialog = (target) => {
    setRejectTarget(target);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectSubmit = () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    if (rejectTarget.type === 'batch') {
      batchRejectMutation.mutate({
        category: rejectTarget.category,
        field_name: rejectTarget.field_name,
        reason: rejectReason,
      });
    } else {
      individualRejectMutation.mutate({
        id: rejectTarget.id,
        reason: rejectReason,
      });
    }
  };

  const groupKey = (cat) =>
    `${cat.category || ''}-${cat.risk_level || ''}-${cat.field_name || ''}`;

  const toggleExpand = (key) => {
    setExpandedGroup(expandedGroup === key ? null : key);
  };

  const isAnyMutating =
    batchApproveMutation.isLoading ||
    batchRejectMutation.isLoading ||
    individualApproveMutation.isLoading ||
    individualRejectMutation.isLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <CircularProgress />
      </div>
    );
  }

  if (!Array.isArray(groups) || groups.length === 0) {
    return <Alert severity="info">No pending change requests found.</Alert>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h3 className="text-lg font-semibold text-gray-700">
          Certificate Change Requests
        </h3>
        <Chip
          label={`${totalPending} pending`}
          color="warning"
          size="small"
          variant="outlined"
        />
      </div>

      {groups.map((cat) => {
        const key = groupKey(cat);
        const categoryName = cat.category || '';
        const riskLevel = cat.risk_level || 'NONE';
        const fieldName = cat.field_name || '';
        const count = cat.count || 0;
        const samples = cat.samples || [];
        const isExpanded = expandedGroup === key;
        const fieldLabel = FIELD_LABELS[fieldName] || fieldName;

        return (
          <Card key={key} variant="outlined">
            <CardContent>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Chip
                  label={fieldLabel}
                  size="small"
                  sx={{
                    backgroundColor:
                      fieldName === 'SPECIALIZATION' ? '#e0f2fe' : '#f3e8ff',
                    color:
                      fieldName === 'SPECIALIZATION' ? '#0369a1' : '#7c3aed',
                    fontWeight: 600,
                  }}
                />
                <Typography variant="subtitle1" component="span">
                  {categoryName.replace(/_/g, ' ')}
                </Typography>
                <Chip
                  label={riskLevel}
                  color={RISK_COLORS[riskLevel] || 'default'}
                  size="small"
                />
                <Typography variant="body2" color="text.secondary">
                  ({count} request{count !== 1 ? 's' : ''})
                </Typography>
              </div>

              <Button
                size="small"
                onClick={() => toggleExpand(key)}
                endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {isExpanded ? 'Collapse' : `Review All (${count})`}
              </Button>

              {!isExpanded && samples.length > 0 && (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ mt: 1 }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Student</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Email</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Current</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Requested</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Cert Status</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {samples.slice(0, 5).map((req, idx) => (
                        <TableRow key={req.id || idx}>
                          <TableCell>{req.student_name || '-'}</TableCell>
                          <TableCell>
                            <span className="text-xs text-gray-500">
                              {req.student_email || '-'}
                            </span>
                          </TableCell>
                          <TableCell>{req.current_value || '-'}</TableCell>
                          <TableCell>{req.requested_value || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={
                                STATUS_LABELS[req.certificate_status] ||
                                req.certificate_status ||
                                '-'
                              }
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {samples.length > 5 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              +{samples.length - 5} more — expand to see all
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Collapse in={isExpanded}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ mt: 1, maxHeight: 500, overflow: 'auto' }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Student</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Email</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Current</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Requested</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Reason</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Cert Status</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {samples.map((req, idx) => (
                        <TableRow key={req.id || idx}>
                          <TableCell>{req.student_name || '-'}</TableCell>
                          <TableCell>
                            <span className="text-xs text-gray-500">
                              {req.student_email || '-'}
                            </span>
                          </TableCell>
                          <TableCell>{req.current_value || '-'}</TableCell>
                          <TableCell>{req.requested_value || '-'}</TableCell>
                          <TableCell>
                            <span className="text-xs">{req.reason || '-'}</span>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                STATUS_LABELS[req.certificate_status] ||
                                req.certificate_status ||
                                '-'
                              }
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() =>
                                individualApproveMutation.mutate(req.id)
                              }
                              disabled={isAnyMutating}
                              title="Approve"
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleOpenRejectDialog({
                                  type: 'individual',
                                  id: req.id,
                                })
                              }
                              disabled={isAnyMutating}
                              title="Reject"
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </CardContent>

            <CardActions sx={{ px: 2, pb: 2 }}>
              <Button
                variant="contained"
                size="small"
                color="success"
                onClick={() => handleBatchApprove(categoryName, fieldName)}
                disabled={isAnyMutating}
              >
                {batchApproveMutation.isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  `Approve All ${count}`
                )}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() =>
                  handleOpenRejectDialog({
                    type: 'batch',
                    category: categoryName,
                    field_name: fieldName,
                  })
                }
                disabled={isAnyMutating}
              >
                Reject All {count}
              </Button>
            </CardActions>
          </Card>
        );
      })}

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Batch Approve</DialogTitle>
        <DialogContent>
          Are you sure you want to approve all{' '}
          <strong>
            {confirmTarget?.category?.replace(/_/g, ' ')} (
            {FIELD_LABELS[confirmTarget?.field_name] ||
              confirmTarget?.field_name}
            )
          </strong>{' '}
          requests? This will update the certificates directly.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmBatchApprove}
            disabled={batchApproveMutation.isLoading}
          >
            {batchApproveMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Approve All'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Reason</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason for rejection"
            fullWidth
            size="small"
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectSubmit}
            disabled={
              batchRejectMutation.isLoading ||
              individualRejectMutation.isLoading
            }
          >
            {batchRejectMutation.isLoading ||
            individualRejectMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Reject'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ChangeRequestDashboard;
